import React, { Dispatch, FC, SetStateAction, useState } from 'react'
import { ActionIcon, Box, Button, Group, Stack, Text, TextInput, Textarea, Tooltip } from '@mantine/core'
import { FilterGroup, SearchFilter, VectorSearchFilters } from '../../../../lib/types'
import { hasRequiredFilters } from '../helpers'
import { SearchablePillInput } from '../SearchablePillInput'
import { IconQuestionMark } from '@tabler/icons-react'
import {useTypeAheadFilters} from '../../../../hooks/ai/use-type-ahead-filters'

interface AddEditFiltersProps {
  selectedFilters: VectorSearchFilters
  setSelectedFilters: (filters: VectorSearchFilters) => void
  updatedFilters: VectorSearchFilters
  setUpdatedFilters: Dispatch<SetStateAction<VectorSearchFilters>>
  onClose: () => void
  currency: string
}

export const AddEditFilters: FC<AddEditFiltersProps> = ({ updatedFilters, setUpdatedFilters, setSelectedFilters, onClose , currency}) => {
  const [showRequiredError, setShowRequiredError] = useState<boolean>(false)


  const pillInputs: { label: string, term: FilterGroup }[] = [
    { label: 'Publisher', term: 'publisher' },
    { label: 'Survey', term: 'surveyCodeSurveyShortName' }
  ]

  const handleTextChange = (field: 'jobTitle' | 'jobDescription', value: string) => {
    setShowRequiredError(false)
    setUpdatedFilters(prev => ({ ...prev, [field]: value }))
  }
  
  const handleApply = () => {
    if (hasRequiredFilters(updatedFilters)) {
      setSelectedFilters(updatedFilters)
      onClose()
    } else {
      setShowRequiredError(true)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') handleApply()
  }

  const {data: searchFilters} = useTypeAheadFilters(currency, updatedFilters)

  return (
    <Stack h='100%' justify='space-between'>
      <Box>
        <TextInput
          mt={25}
          label={
          <Group gap={3} justify='space-between'>
            Job Title
            <Tooltip label='Job Title has been pre-populated with your organization Job Title. You can edit or remove text here.'>
              <ActionIcon radius='xl' variant='transparent'>
                <IconQuestionMark style={{ height: 14, width: 14 }} />
              </ActionIcon>
            </Tooltip>
          </Group>
          }
          placeholder='Job Title'
          value={updatedFilters.jobTitle ?? ''}
          error={showRequiredError}
          onKeyDown={handleKeyDown}
          onChange={e => handleTextChange('jobTitle', e.currentTarget.value)}
        />
        <Textarea
          label={
              <Group gap={5}>
                Job Description
                <Tooltip maw={750} multiline label='Input a job summary or key responsibilities for searching. If you prefer, you can use an existing match description by clicking on “Copy description from matches” above.'>
                  <ActionIcon radius='xl' variant='transparent'>
                    <IconQuestionMark style={{ height: 14, width: 14 }} />
                  </ActionIcon>
                </Tooltip>
              </Group>
          }
          placeholder='Job Description'
          rows={5}
          value={updatedFilters.jobDescription ?? ''}
          error={showRequiredError}
          onKeyDown={handleKeyDown}
          onChange={e => handleTextChange('jobDescription', e.currentTarget.value)}
        />
        {pillInputs.map(({ term, label }) =>
          <SearchablePillInput
            key={label}
            updatedFilters={updatedFilters}
            setUpdatedFilters={setUpdatedFilters}
            term={term}
            label={label}
            searchFields={searchFilters ? searchFilters[term] : []}
          />
        )}
        { showRequiredError && <Text ta='center' c='red.6'>Job title or description is required.</Text> }
      </Box>
      <Group justify='flex-end' mt={100}>
        <Button onClick={onClose} variant='outline'>Close</Button>
        <Button onClick={handleApply}>
          Apply
        </Button>
      </Group>
    </Stack>
  )
}
